import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
	"inline-flex items-center rounded-full border border-zinc-200 px-2.5 py-0.5 text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 dark:border-zinc-800 dark:focus:ring-zinc-300",
	{
		variants: {
			variant: {
				default:
					"border-transparent bg-zinc-900 text-zinc-50 shadow hover:bg-zinc-900/80 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-zinc-50/80",
				secondary:
					"border-transparent bg-zinc-100 text-zinc-900 hover:bg-zinc-100/80 dark:bg-zinc-800 dark:text-zinc-50 dark:hover:bg-zinc-800/80",
				destructive:
					"border-transparent bg-red-500 text-zinc-50 shadow hover:bg-red-500/80 dark:bg-red-900 dark:text-zinc-50 dark:hover:bg-red-900/80",
				outline: "text-zinc-950 dark:text-zinc-50",
				purple: "bg-purple-50 text-purple-500 border-purple-200",
				blue: "bg-blue-50 text-blue-500 border-blue-200",

				green:
					"bg-green-50 text-green-500 border-green-200 !hover:bg-green-100",
				yellow: "bg-yellow-50 text-yellow-500 border-yellow-200",
				red: "bg-red-50 text-red-500 border-red-200",
				status: "px-1.5 py-0 text-white border-none",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

function Badge({
	className,
	variant,
	asChild = false,
	render: renderProp,
	children,
	...props
}: useRender.ComponentProps<"span"> &
	VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
	const effectiveRender =
		asChild && React.isValidElement(children) ? children : renderProp;

	return useRender({
		defaultTagName: "span",
		props: mergeProps<"span">(
			{
				className: cn(badgeVariants({ variant }), className),
			},
			{ ...props, children: effectiveRender ? undefined : children },
		),
		render: effectiveRender,
		state: {
			slot: "badge",
			variant,
		},
	});
}

export { Badge };
